import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import {
  getPersonalTasks, savePersonalTask, deletePersonalTask,
  GetPersonalTasksOutputType,
} from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, CheckCircle2, Circle, Clock, Archive, Loader2, Trash2, ChevronRight, AlertTriangle, Flag } from 'lucide-react';
import { format, parseISO, isPast, isToday, addDays, isBefore } from 'date-fns';

type Task = GetPersonalTasksOutputType['tasks'][0];

const priorityConfig = {
  High: { color: 'text-destructive', bg: 'bg-destructive/10', icon: AlertTriangle },
  Medium: { color: 'text-primary', bg: 'bg-primary/10', icon: Flag },
  Low: { color: 'text-muted-foreground', bg: 'bg-muted', icon: Flag },
};

const statusIcon: Record<string, React.FC<{ className?: string }>> = {
  'To Do': Circle,
  'In Progress': Clock,
  'Done': CheckCircle2,
  'Archived': Archive,
};

function fmtDate(d?: string) {
  if (!d) return '';
  try { return format(parseISO(d.includes('T') ? d : d + 'T00:00:00'), 'dd MMM'); } catch { return d; }
}

function dueBadge(d?: string) {
  if (!d) return null;
  try {
    const date = parseISO(d.includes('T') ? d : d + 'T00:00:00');
    if (isPast(date) && !isToday(date)) return <span className="text-[10px] px-1.5 py-0.5 rounded bg-destructive/10 text-destructive font-medium">Overdue</span>;
    if (isToday(date)) return <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">Today</span>;
    if (isBefore(date, addDays(new Date(), 3))) return <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent/10 text-accent-foreground font-medium">Soon</span>;
  } catch {}
  return null;
}

export default function PersonalTasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [counts, setCounts] = useState({ todo: 0, inProgress: 0, done: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTask, setEditTask] = useState<Task | null>(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState<string>('active');

  const [form, setForm] = useState({ title: '', notes: '', status: 'To Do', priority: 'Medium', dueDate: '' });

  const load = () => {
    if (!user) return;
    getPersonalTasks({ includeArchived: showArchived })
      .then(d => { setTasks(d.tasks); setCounts(d.counts); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [user, showArchived]);

  const openNew = () => {
    setEditTask(null);
    setForm({ title: '', notes: '', status: 'To Do', priority: 'Medium', dueDate: '' });
    setDialogOpen(true);
  };

  const openEdit = (t: Task) => {
    setEditTask(t);
    setForm({ title: t.title, notes: t.notes || '', status: t.status, priority: t.priority, dueDate: t.dueDate || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      await savePersonalTask({
        id: editTask?.id,
        title: form.title,
        notes: form.notes || undefined,
        status: form.status as any,
        priority: form.priority as any,
        dueDate: form.dueDate || undefined,
      });
      toast.success(editTask ? 'Task updated' : 'Task created');
      setDialogOpen(false);
      load();
    } catch (e: any) { toast.error(e.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleQuickComplete = async (t: Task) => {
    const newStatus = t.status === 'Done' ? 'To Do' : 'Done';
    try {
      await savePersonalTask({ id: t.id, title: t.title, status: newStatus as any, priority: t.priority as any });
      setTasks(prev => prev.map(x => x.id === t.id ? { ...x, status: newStatus } : x));
      toast.success(newStatus === 'Done' ? 'Task completed!' : 'Task reopened');
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (id: string) => {
    try {
      await deletePersonalTask({ id });
      setTasks(prev => prev.filter(x => x.id !== id));
      toast.success('Task deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const filtered = tasks.filter(t => {
    if (filter === 'active') return t.status !== 'Done' && t.status !== 'Archived';
    if (filter === 'done') return t.status === 'Done';
    if (filter === 'archived') return t.status === 'Archived';
    return true;
  }).sort((a, b) => {
    const pOrder = { High: 0, Medium: 1, Low: 2 };
    return (pOrder[a.priority as keyof typeof pOrder] ?? 1) - (pOrder[b.priority as keyof typeof pOrder] ?? 1);
  });

  if (loading) return (
    <div className="px-4 md:px-8 py-6 max-w-2xl mx-auto space-y-3 pb-20 md:pb-6">
      <Skeleton className="h-8 w-48" />
      {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16" />)}
    </div>
  );

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-6">
      <div className="bg-card border-b border-border">
        <div className="px-4 md:px-8 py-4 md:py-6 flex items-center justify-between">
          <div>
            <h1 className="text-lg md:text-xl font-bold text-foreground">My Tasks</h1>
            <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
              {counts.todo + counts.inProgress} active · {counts.done} done
            </p>
          </div>
          <Button onClick={openNew} size="sm" className="bg-primary text-primary-foreground">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
      </div>

      <div className="px-4 md:px-8 py-4 max-w-2xl mx-auto">
        {/* Filter tabs */}
        <div className="flex gap-1 mb-4 bg-muted rounded-lg p-1">
          {[
            { key: 'active', label: `Active (${counts.todo + counts.inProgress})` },
            { key: 'done', label: `Done (${counts.done})` },
            { key: 'archived', label: 'Archived' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => { setFilter(f.key); if (f.key === 'archived') setShowArchived(true); }}
              className={`flex-1 text-xs font-medium py-1.5 rounded transition-colors ${
                filter === f.key ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Task list */}
        {filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">{filter === 'active' ? 'No active tasks — nice work!' : 'Nothing here yet.'}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(t => {
              const StatusIcon = statusIcon[t.status] || Circle;
              const pCfg = priorityConfig[t.priority as keyof typeof priorityConfig] || priorityConfig.Medium;
              return (
                <div
                  key={t.id}
                  className="border border-border rounded-lg bg-card px-3 py-2.5 flex items-center gap-3 group"
                >
                  <button
                    onClick={() => handleQuickComplete(t)}
                    className="shrink-0 touch-manipulation"
                    title={t.status === 'Done' ? 'Reopen' : 'Complete'}
                  >
                    <StatusIcon className={`w-5 h-5 ${t.status === 'Done' ? 'text-primary' : 'text-muted-foreground hover:text-primary'} transition-colors`} />
                  </button>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openEdit(t)}>
                    <p className={`text-sm font-medium truncate ${t.status === 'Done' ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                      {t.title}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${pCfg.bg} ${pCfg.color}`}>
                        {t.priority}
                      </span>
                      {t.dueDate && <span className="text-[10px] text-muted-foreground">{fmtDate(t.dueDate)}</span>}
                      {dueBadge(t.dueDate)}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Task dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editTask ? 'Edit Task' : 'New Task'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-1.5">Title *</label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="What needs doing?" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-1.5">Notes</label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Any extra details..." className="min-h-16" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-1.5">Priority</label>
                <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-1.5">Status</label>
                <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="To Do">To Do</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Done">Done</SelectItem>
                    <SelectItem value="Archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-1.5">Due Date</label>
              <Input type="date" value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-primary text-primary-foreground">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : editTask ? 'Update Task' : 'Create Task'}
              </Button>
              {editTask && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="icon" className="shrink-0"><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete task?</AlertDialogTitle>
                      <AlertDialogDescription>This will permanently remove "{editTask.title}".</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => { handleDelete(editTask.id); setDialogOpen(false); }} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
